/* istanbul ignore file */
import currency, { Options } from 'currency.js'

import config from '@config'
import i18n from '@lib/i18n'
import timezoneCurrencyMap from '@lib/timezonCurrencyMap'
import utils from '@lib/utils'
import exchangeRatesStore from '@stores/exchangeRates'

type CurrencyFormatMap = Record<
  string,
  {
    symbol: string
    name: string
    multiplier: number
    decimal?: string
    precision?: number
    separator?: string
  }
>
const EXCLUDE_CURRENCIES = ['MXN']

const MAP: CurrencyFormatMap = {
  ALL: { symbol: 'L', name: 'ALL', multiplier: 1 },
  AED: { symbol: 'د.إ', name: 'AED', multiplier: 1 },
  ARS: { symbol: 'AR$', name: 'ARS', multiplier: 1 },
  AUD: { symbol: 'AU$', name: 'AUD', multiplier: 1 },
  AZN: { symbol: '₼', name: 'AZN', multiplier: 1 },
  BAM: { symbol: 'KM', name: 'BAM', multiplier: 1 },
  BGN: { symbol: 'ЛВ.', name: 'BGN', multiplier: 1 },
  BRL: { symbol: 'R$', name: 'BRL', multiplier: 1 },
  CAD: { symbol: 'C$', name: 'CAD', multiplier: 1 },
  CHF: { symbol: 'CHF', name: 'CHF', multiplier: 1 },
  CNY: { symbol: '¥', name: 'CNY', multiplier: 1 },
  COP: { symbol: 'CO$', name: 'COP', multiplier: 1 },
  CZK: { symbol: 'Kč', name: 'CZK', multiplier: 1 },
  DKK: { symbol: 'kr.', name: 'DKK', multiplier: 1 },
  EGP: { symbol: 'E£', name: 'EGP', multiplier: 1 },
  EUR: { symbol: '€', name: 'EUR', multiplier: 1 },
  GBP: { symbol: '£', name: 'GBP', multiplier: 1 },
  GEL: { symbol: '₾', name: 'GEL', multiplier: 1 },
  HKD: { symbol: 'HK$', name: 'HKD', multiplier: 1 },
  HRK: { symbol: 'kn', name: 'HRK', multiplier: 1 },
  HUF: { symbol: 'Ft', name: 'HUF', multiplier: 100 },
  IDR: { symbol: 'Rp', name: 'IDR', multiplier: 1, precision: 2 },
  INR: { symbol: '₹', name: 'INR', multiplier: 1 },
  JPY: { symbol: '¥', name: 'JPY', multiplier: 1, precision: 0 },
  KES: { symbol: 'Kshs ', name: 'KES', multiplier: 0.01, precision: 0 },
  KRW: { symbol: '₩', name: 'KRW', multiplier: 1, precision: 0 },
  KZT: { symbol: '₸', name: 'KZT', multiplier: 1 },
  LAK: { symbol: '₭', name: 'LAK', multiplier: 1 },
  LKR: { symbol: 'Rs', name: 'LKR', multiplier: 1 },
  MAD: { symbol: 'MAD', name: 'MAD', multiplier: 1 },
  MKD: { symbol: 'ден', name: 'MKD', multiplier: 1 },
  MMK: { symbol: 'K', name: 'MMK', multiplier: 1 },
  MXN: { symbol: 'MX$', name: 'MXN', multiplier: 1 },
  MYR: { symbol: 'RM', name: 'MYR', multiplier: 1 },
  NGN: { symbol: '₦', name: 'NGN', multiplier: 1 },
  NOK: { symbol: 'kr', name: 'NOK', multiplier: 1 },
  NZD: { symbol: 'NZ$', name: 'NZD', multiplier: 1 },
  NPR: { symbol: 'Rs', name: 'NPR', multiplier: 1 },
  PHP: { symbol: '₱', name: 'PHP', multiplier: 1 },
  PKR: { symbol: 'Rs', name: 'PKR', multiplier: 1 },
  PLN: { symbol: 'zł', name: 'PLN', multiplier: 1 },
  RON: { symbol: 'L', name: 'RON', multiplier: 1 },
  RSD: { symbol: 'дин.', name: 'RSD', multiplier: 1 },
  RUB: { symbol: '₽', name: 'RUB', multiplier: 1 },
  SAR: { symbol: '﷼', name: 'SAR', multiplier: 1 },
  SEK: { symbol: 'kr', name: 'SEK', multiplier: 1 },
  SGD: { symbol: 'S$', name: 'SGD', multiplier: 1 },
  THB: { symbol: '฿', name: 'THB', multiplier: 1 },
  TRY: { symbol: '₺', name: 'TRY', multiplier: 1 },
  TWD: { symbol: '元', name: 'TWD', multiplier: 1 },
  UAH: { symbol: '₴', name: 'UAH', multiplier: 1 },
  USD: { symbol: 'US$', name: 'USD', multiplier: 1 },
  VND: { symbol: '₫', name: 'VND', multiplier: 1, precision: 0 },
  CLP: { symbol: '$', name: 'CLP', multiplier: 1, precision: 0 },
}

const getSymbol = (currency: Currency): string => {
  const code = currency.toUpperCase()

  return MAP[code]?.symbol ?? code
}

const getName = (currency: Currency): string | null => {
  const code = currency.toUpperCase()

  return MAP[code]?.name
}

const getMultiplier = (currency: Currency | undefined): number => {
  if (currency) return MAP[currency.toUpperCase()]?.multiplier ?? 1
  return 1
}

const getPrecision = (currency: Currency | undefined): number => {
  if (currency) return MAP[currency.toUpperCase()]?.precision ?? 2
  return 2
}

const sort = (supported: Currency[]): Currency[] => {
  const filteredTop = config.settings.topCurrencies.filter(item => supported.includes(item))
  const restCurrencies = supported.filter(item => !filteredTop.includes(item)).sort((a, b) => (a > b ? 1 : -1))

  return [...filteredTop, ...restCurrencies]
}

const verifyPositionInPopularList = (supported: Currency[], code: Currency, index: number): boolean => {
  const filteredTop = config.settings.topCurrencies.filter(item => supported.includes(item))

  return filteredTop.at(index) === code
}

export const createCurrency = (price: number, code?: Currency, opt?: Options & { locale?: Locale }): currency => {
  const currencyCode = code?.toUpperCase() as Currency
  const multiplier = getMultiplier(currencyCode)
  const precision = getPrecision(currencyCode)
  const currentLocale = opt?.locale || i18n.getLanguage() || 'en-US'
  const currencySign = code ? getSymbol(code) : ''

  const options = {
    fromCents: true,
    precision: precision,
    symbol: currencySign,
    ...opt,
  }

  const formatter = new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: currencyCode || 'USD',
    currencyDisplay: 'symbol',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: true,
  })

  const format = (curr?: currency) => {
    if (code) {
      return formatter.format(curr?.value ?? 0)
    }
    return curr && curr.value ? curr.value.toString() : ''
  }

  if (!EXCLUDE_CURRENCIES.includes(currencyCode)) {
    options.format = format
  }
  return currency(price, options).multiply(multiplier)
}

const getExchangeRate = (currency: Currency): number | undefined =>
  exchangeRatesStore.get().rates.find(({ code }) => code === currency)?.exchangeRate

const formatPrice = (fractional: number): number => createCurrency(fractional).value

const getPriceInEuro = (price: Money): number => {
  const exchangeRate = getExchangeRate(price.currency)
  const fractional = Math.round(price.fractional / Number(exchangeRate))

  return formatPrice(fractional)
}

const getBestCurrency = (supported: Currency[], defaultValue: Currency): Currency => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const countryCurrency = timezoneCurrencyMap[timezone]

  return utils.array.containsOrDefault(countryCurrency, supported, defaultValue)
}

const sumPrice = (first: Money, second: Money): Money => ({
  fractional: first.fractional + second.fractional,
  currency: first.currency,
})

const getPrecisionMultiplier = (currency: Currency): number => 10 ** getPrecision(currency)
const convertDecimalToInteger = (price: Money): number => getPrecisionMultiplier(price.currency) * price.fractional

const currencyUtils = {
  getSymbol,
  getName,
  sort,
  verifyPositionInPopularList,
  create: createCurrency,
  getPriceInEuro,
  formatPrice,
  getBestCurrency,
  getMultiplier,
  getPrecision,
  sumPrice,
  convertDecimalToInteger,
  getPrecisionMultiplier,
}

export default currencyUtils
